import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import { createElement } from "@wordpress/element";
/**
 * External dependencies
 */

import classnames from 'classnames';
/**
 * WordPress dependencies
 */

import { compose } from '@wordpress/compose';
import { createBlock } from '@wordpress/blocks';
import { withDispatch, withSelect } from '@wordpress/data';
import { ExternalLink, KeyboardShortcuts, PanelBody, Path, SVG, TextareaControl, TextControl, Toolbar, ToggleControl, ToolbarButton } from '@wordpress/components';
import { LEFT, RIGHT, UP, DOWN, BACKSPACE, ENTER, rawShortcut, displayShortcut } from '@wordpress/keycodes';
import { __ } from '@wordpress/i18n';
import { BlockControls, InnerBlocks, InspectorControls, RichText, __experimentalLinkControl as LinkControl } from '@wordpress/block-editor';
import { Fragment, useState, useEffect } from '@wordpress/element';
/**
 * It updates the link attribute when the
 * link settings changes.
 *
 * @param {Function} setter Setter attribute function.
 */

var updateLinkSetting = function updateLinkSetting(setter) {
  return function (setting, value) {
    setter(_defineProperty({}, setting, value));
  };
};
/**
 * Updates the link attribute when it changes
 * through of the `onLinkChange` LinkControl callback.
 *
 * @param {Function} setter Setter attribute function.
 * @param {string} label ItemMenu link label.
 */


var updateLink = function updateLink(setter, label) {
  return function () {
    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        _ref$title = _ref.title,
        newTitle = _ref$title === void 0 ? '' : _ref$title,
        _ref$url = _ref.url,
        newURL = _ref$url === void 0 ? '' : _ref$url;

    setter({
      title: newTitle,
      url: newURL
    }); // Set the item label as well if it isn't already defined.

    if (!label) {
      setter({
        label: newTitle
      });
    }
  };
};

function NavigationMenuItemEdit(_ref2) {
  var attributes = _ref2.attributes,
      hasDescendants = _ref2.hasDescendants,
      isSelected = _ref2.isSelected,
      isParentOfSelectedBlock = _ref2.isParentOfSelectedBlock,
      setAttributes = _ref2.setAttributes,
      insertMenuItemBlock = _ref2.insertMenuItemBlock;
  var label = attributes.label,
      opensInNewTab = attributes.opensInNewTab,
      title = attributes.title,
      url = attributes.url;
  var link = title ? {
    title: title,
    url: url
  } : null;

  var _useState = useState(!label && isSelected),
      _useState2 = _slicedToArray(_useState, 2),
      isLinkOpen = _useState2[0],
      setIsLinkOpen = _useState2[1];

  var onCloseTimerId = null;
  /**
   * It's a kind of hack to handle closing the LinkControl popover
   * clicking on the ToolbarButton link.
   */

  useEffect(function () {
    if (!isSelected) {
      setIsLinkOpen(false);
    }

    return function () {
      // Clear LinkControl.OnClose timeout.
      if (onCloseTimerId) {
        clearTimeout(onCloseTimerId);
      }
    };
  }, [isSelected]);
  /**
   * Opens the LinkControl popup
   */

  var openLinkControl = function openLinkControl() {
    if (isLinkOpen) {
      return;
    }

    setIsLinkOpen(!isLinkOpen);
  };
  /**
   * `onKeyDown` LinkControl handler.
   * It takes over to stop the event propagation to make the
   * navigation work, avoiding undesired behaviors.
   * For instance, it will block to move between menu items
   * when the LinkControl is focused.
   *
   * @param {Event} event
   */


  var handleLinkControlOnKeyDown = function handleLinkControlOnKeyDown(event) {
    var keyCode = event.keyCode;

    if ([LEFT, DOWN, RIGHT, UP, BACKSPACE, ENTER].indexOf(keyCode) > -1) {
      // Stop the key event from propagating up to ObserveTyping.startTypingInTextField.
      event.stopPropagation();
    }
  };

  var itemLabelPlaceholder = __('Add item…');

  return createElement(Fragment, null, createElement(BlockControls, null, createElement(Toolbar, null, createElement(KeyboardShortcuts, {
    bindGlobal: true,
    shortcuts: _defineProperty({}, rawShortcut.primary('k'), openLinkControl)
  }), createElement(ToolbarButton, {
    name: "link",
    icon: "admin-links",
    title: __('Link'),
    shortcut: displayShortcut.primary('k'),
    onClick: openLinkControl
  }), createElement(ToolbarButton, {
    name: "submenu",
    icon: createElement(SVG, {
      xmlns: "http://www.w3.org/2000/svg",
      width: "24",
      height: "24"
    }, createElement(Path, {
      d: "M14 5h8v2h-8zm0 5.5h8v2h-8zm0 5.5h8v2h-8zM2 11.5C2 15.08 4.92 18 8.5 18H9v2l3-3-3-3v2h-.5C6.02 16 4 13.98 4 11.5S6.02 7 8.5 7H12V5H8.5C4.92 5 2 7.92 2 11.5z"
    }), createElement(Path, {
      fill: "none",
      d: "M0 0h24v24H0z"
    })),
    title: __('Add submenu item'),
    onClick: insertMenuItemBlock
  }))), createElement(InspectorControls, null, createElement(PanelBody, {
    title: __('Menu Settings')
  }, createElement(ToggleControl, {
    checked: attributes.opensInNewTab,
    onChange: function onChange(newTab) {
      setAttributes({
        opensInNewTab: newTab
      });
    },
    label: __('Open in new tab')
  }), createElement(TextareaControl, {
    value: attributes.description || '',
    onChange: function onChange(description) {
      setAttributes({
        description: description
      });
    },
    label: __('Description')
  })), createElement(PanelBody, {
    title: __('SEO Settings')
  }, createElement(TextControl, {
    value: attributes.title || '',
    onChange: function onChange(itemTitle) {
      setAttributes({
        title: itemTitle
      });
    },
    label: __('Title Attribute'),
    help: __('Provide more context about where the link goes.')
  }), createElement(ToggleControl, {
    checked: attributes.nofollow,
    onChange: function onChange(nofollow) {
      setAttributes({
        nofollow: nofollow
      });
    },
    label: __('Add nofollow to menu item'),
    help: createElement(Fragment, null, __('Don\'t let search engines follow this link.'), createElement(ExternalLink, {
      className: "wp-block-navigation-menu-item__nofollow-external-link",
      href: __('https://codex.wordpress.org/Nofollow')
    }, __('What\'s this?')))
  }))), createElement("div", {
    className: classnames('wp-block-navigation-menu-item', {
      'is-editing': isSelected || isParentOfSelectedBlock,
      'is-selected': isSelected
    })
  }, createElement("div", {
    className: "wp-block-navigation-menu-item__inner"
  }, createElement(RichText, {
    className: "wp-block-navigation-menu-item__content",
    value: label,
    onChange: function onChange(labelValue) {
      return setAttributes({
        label: labelValue
      });
    },
    placeholder: itemLabelPlaceholder,
    withoutInteractiveFormatting: true
  }), isLinkOpen && createElement(LinkControl, {
    className: "wp-block-navigation-menu-item__inline-link-input",
    onKeyDown: handleLinkControlOnKeyDown,
    onKeyPress: function onKeyPress(event) {
      return event.stopPropagation();
    },
    currentLink: link,
    onLinkChange: updateLink(setAttributes, label),
    onClose: function onClose() {
      onCloseTimerId = setTimeout(function () {
        return setIsLinkOpen(false);
      }, 100);
    },
    currentSettings: [{
      id: 'opensInNewTab',
      title: __('Open in New Tab'),
      checked: opensInNewTab
    }],
    onSettingsChange: updateLinkSetting(setAttributes)
  })), createElement(InnerBlocks, {
    allowedBlocks: ['core/navigation-menu-item'],
    renderAppender: hasDescendants ? InnerBlocks.ButtonBlockAppender : false
  })));
}

export default compose([withSelect(function (select, ownProps) {
  var _select = select('core/block-editor'),
      getClientIdsOfDescendants = _select.getClientIdsOfDescendants,
      hasSelectedInnerBlock = _select.hasSelectedInnerBlock;

  var clientId = ownProps.clientId;
  return {
    isParentOfSelectedBlock: hasSelectedInnerBlock(clientId, true),
    hasDescendants: !!getClientIdsOfDescendants([clientId]).length
  };
}), withDispatch(function (dispatch, ownProps, registry) {
  return {
    insertMenuItemBlock: function insertMenuItemBlock() {
      var clientId = ownProps.clientId;

      var _dispatch = dispatch('core/block-editor'),
          insertBlock = _dispatch.insertBlock;

      var _registry$select = registry.select('core/block-editor'),
          getClientIdsOfDescendants = _registry$select.getClientIdsOfDescendants;

      var navItems = getClientIdsOfDescendants([clientId]);
      var insertionPoint = navItems.length ? navItems.length : 0;
      var blockToInsert = createBlock('core/navigation-menu-item');
      insertBlock(blockToInsert, insertionPoint, clientId);
    }
  };
})])(NavigationMenuItemEdit);